import React from "react";
import { Sigma } from "lucide-react";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../design-system/components/composite/Modal/Modal";
import { Button } from "../../../design-system/components/primitives/Button/Button";
import { Spinner } from "../../atoms/Spinner";
import { DataStudioColumnDetailsForm } from "../../molecules/DataStudioColumnDetailsForm";
import { DataStudioColumnFactsPanel } from "../../molecules/DataStudioColumnFactsPanel";
import {
  ErrorRegion,
  HeaderIcon,
  HeaderIdentity,
  HeaderMetadata,
  HeaderText,
  HeaderTitle,
  MetadataPill,
  MetadataText,
  ModalShell,
  PanelsGrid,
  StateRegion,
  UpdateButton,
  UpdateError,
} from "./DataStudioColumnDetailsModal.styles";
import type { DataStudioColumnDetailsModalProps } from "./DataStudioColumnDetailsModal.types";
import { useDataStudioColumnDetailsModalInternalState } from "./useDataStudioColumnDetailsModalInternalState";

const TITLE_ID = "data-studio-column-details-title";

export const DataStudioColumnDetailsModal: React.FC<
  DataStudioColumnDetailsModalProps
> = ({ open, connectionId, assetId, columnId, table, onClose, onUpdated }) => {
  const state = useDataStudioColumnDetailsModalInternalState({
    open,
    connectionId,
    assetId,
    columnId,
    onClose,
    onUpdated,
  });
  const details = state.data;

  return (
    <ModalShell
      open={open}
      onClose={state.close}
      size="xl"
      ariaLabelledBy={TITLE_ID}
      closable={!state.isUpdating}
      closeOnBackdropClick={!state.isUpdating}
      closeOnEscape={!state.isUpdating}
    >
      <ModalHeader
        onClose={state.close}
        padding="0.75rem"
        minHeight="4rem"
      >
        <HeaderIdentity>
          <HeaderIcon><Sigma aria-hidden="true" /></HeaderIcon>
          <HeaderText>
            <HeaderTitle id={TITLE_ID}>
              {details?.column.name ?? (columnId || "Column details")}
            </HeaderTitle>
            {details ? (
              <HeaderMetadata aria-label="Column summary">
                <MetadataText>{table.name}</MetadataText>
                <MetadataText $separator>{table.sourceName}</MetadataText>
                {typeof table.rowCount === "number" ? (
                  <MetadataText $separator>
                    {table.rowCount.toLocaleString()} rows
                  </MetadataText>
                ) : null}
                <MetadataPill>{details.column.semanticRole}</MetadataPill>
                <MetadataPill>{details.column.dataType}</MetadataPill>
              </HeaderMetadata>
            ) : null}
          </HeaderText>
        </HeaderIdentity>
      </ModalHeader>
      <ModalBody padding="0.75rem" overflowY="hidden">
        {state.isLoading ? (
          <StateRegion role="status" aria-live="polite">
            <Spinner size={24} /> Loading column details...
          </StateRegion>
        ) : state.loadErrorMessage ? (
          <ErrorRegion role="alert">
            <span>{state.loadErrorMessage}</span>
            <Button type="button" variant="secondary" onClick={() => state.refetch()}>
              Try again
            </Button>
          </ErrorRegion>
        ) : details ? (
          <PanelsGrid>
            {state.updateErrorMessage ? (
              <UpdateError role="alert">{state.updateErrorMessage}</UpdateError>
            ) : null}
            <DataStudioColumnFactsPanel
              facts={details.facts}
              ariaLabel={`${details.column.name} profile facts`}
            />
            <DataStudioColumnDetailsForm
              description={state.description}
              onDescriptionChange={state.setDescription}
              synonyms={state.synonyms}
              synonymsDraft={state.synonymInput.draft}
              synonymsInputError={state.synonymInput.error}
              onSynonymsChange={state.synonymInput.handleChange}
              onSynonymsKeyDown={state.synonymInput.handleKeyDown}
              onSynonymsBlur={state.synonymInput.handleBlur}
              onRemoveSynonym={state.removeSynonym}
              notes={state.notes}
              noteInput={state.noteInput.draft}
              onNoteInputChange={(value) =>
                state.noteInput.handleChange({
                  target: { value },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              onNoteKeyDown={state.noteInput.handleKeyDown}
              onRemoveNote={state.removeNote}
              disabled={state.isUpdating}
            />
          </PanelsGrid>
        ) : null}
      </ModalBody>
      <ModalFooter padding="0.75rem">
        <UpdateButton
          type="button"
          variant="primary-dark"
          loading={state.isUpdating}
          disabled={!state.canUpdate}
          onClick={state.submit}
        >
          Update
        </UpdateButton>
      </ModalFooter>
    </ModalShell>
  );
};

export default DataStudioColumnDetailsModal;
